import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { setAccessToken, setUnauthorizedHandler } from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Persist session entirely in memory + httpOnly refresh cookie (no localStorage).
  const setSession = useCallback((data) => {
    if (data?.access_token && data?.user) {
      setAccessToken(data.access_token);
      setUser(data.user);
    } else {
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch (err) {
      // Ignore logout API errors; local session is cleared regardless.
    }
    setAccessToken(null);
    setUser(null);
  }, []);

  // When a refresh ultimately fails, treat the user as logged out.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
  }, []);

  // Bootstrap session on mount using the httpOnly refresh cookie.
  useEffect(() => {
    const bootstrap = async () => {
      try {
        const resp = await api.post('/auth/refresh');
        setAccessToken(resp.data.access_token);
        setUser(resp.data.user);
      } catch (err) {
        setAccessToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, setSession, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
